import React, { FC, useMemo } from 'react';
import { AddLiquidityRangeSelector } from '@app/screens/liquidity/AddLiquidityV3/components/AddLiquidityRangeSelector';
import AddLiquidityInputAmounts from '@app/screens/liquidity/AddLiquidityV3/components/AddLiquidityInputAmounts/AddLiquidityInputAmounts';
import AddLiquidityButton from '@app/screens/liquidity/AddLiquidityV3/components/AddLiquidityButton';
import {
  useDerivedMintInfo,
  useMintActionHandlers,
  useMintState
} from '@app/state/mintStore';
import {
  Bound,
  Currency,
  encodeSqrtRatioX96,
  INITIAL_POOL_FEE
} from '@cryptoalgebra/kim-sdk';

interface Props {
  poolAddress: string | undefined;
  currencyA: Currency | undefined;
  currencyB: Currency | undefined;
}

export const AddLiquidityManual: FC<Props> = ({
  poolAddress,
  currencyA,
  currencyB
}) => {
  const mintInfo = useDerivedMintInfo(
    currencyA ?? undefined,
    currencyB ?? undefined,
    poolAddress ?? undefined,
    INITIAL_POOL_FEE,
    currencyA ?? undefined,
    undefined
  );

  const { startPriceTypedValue, mode, strategy, provider } = useMintState();

  const { [Bound.LOWER]: priceLower, [Bound.UPPER]: priceUpper } =
    mintInfo.pricesAtTicks;

  const price = useMemo(() => {
    if (!mintInfo.price) return;

    return mintInfo.invertPrice ? mintInfo.price.invert() : mintInfo.price;
  }, [mintInfo]);

  const { onStartPriceInput, onFieldBInput, onFieldAInput } =
    useMintActionHandlers(mintInfo.noLiquidity);

  const sqrtRatioX96 = mintInfo.price
    ? encodeSqrtRatioX96(mintInfo.price.numerator, mintInfo.price.denominator)
    : undefined;

  return (
    <>
      <AddLiquidityRangeSelector
        onStartPriceInput={onStartPriceInput}
        mintInfo={mintInfo}
        startPriceTypedValue={startPriceTypedValue}
        price={price}
        currencyB={currencyB}
        currencyA={currencyA}
      />
      <AddLiquidityInputAmounts
        price={price}
        mintInfo={mintInfo}
        currencyA={currencyA}
        currencyB={currencyB}
      />
      <AddLiquidityButton
        baseCurrency={currencyA}
        quoteCurrency={currencyB}
        mintInfo={mintInfo}
        poolAddress={poolAddress}
      />
    </>
  );
};
